import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Briefcase, MessageSquare, FileText, Users, Trash2 } from 'lucide-react';
import { ShareFilterType, SharedResource } from '@/types/sharing';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Skeleton } from '@/components/ui/skeleton';
import { UnshareConfirmDialog } from './UnshareConfirmDialog';
import { useUnshareResource } from '@/hooks/useUnshareResource';
import { ActionButton } from '@/components/ui/action-button';

interface SharedResourcesListProps {
  resources: SharedResource[] | undefined;
  isLoading: boolean;
  filter: ShareFilterType;
  onFilterChange: (filter: ShareFilterType) => void;
}

const getResourceIcon = (type: string) => {
  switch (type) {
    case 'caso':
      return Briefcase;
    case 'chat':
      return MessageSquare;
    case 'document':
      return FileText;
    default:
      return FileText;
  }
};

const getResourceColor = (type: string) => {
  switch (type) {
    case 'caso':
      return 'text-blue-600 bg-blue-50 dark:bg-blue-950';
    case 'chat':
      return 'text-green-600 bg-green-50 dark:bg-green-950';
    case 'document':
      return 'text-purple-600 bg-purple-50 dark:bg-purple-950';
    default:
      return 'text-gray-600 bg-gray-50 dark:bg-gray-950';
  }
};

export function SharedResourcesList({
  resources,
  isLoading,
  filter,
  onFilterChange,
}: SharedResourcesListProps) {
  const [unshareDialog, setUnshareDialog] = useState<{
    open: boolean;
    resource: SharedResource | null;
  }>({ open: false, resource: null });

  const unshareResource = useUnshareResource();

  const handleDeleteClick = (resource: SharedResource) => {
    setUnshareDialog({ open: true, resource });
  };

  const handleConfirmUnshare = () => {
    if (!unshareDialog.resource) return;
    
    const { type } = unshareDialog.resource;
    if (type !== 'caso' && type !== 'chat') return;

    unshareResource.mutate({
      resourceId: unshareDialog.resource.resourceId,
      resourceType: type,
    });

    setUnshareDialog({ open: false, resource: null });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recursos Compartilhados</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Recursos Compartilhados</CardTitle>
          <Select value={filter} onValueChange={(v) => onFilterChange(v as ShareFilterType)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="casos">Casos</SelectItem>
              <SelectItem value="chats">Chats</SelectItem>
              <SelectItem value="documents">Documentos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {!resources || resources.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            Nenhum recurso compartilhado encontrado
          </p>
        ) : (
          <div className="space-y-3">
            {resources.map((resource) => {
              const Icon = getResourceIcon(resource.type);
              const colorClass = getResourceColor(resource.type);

              return (
                <div
                  key={resource.id}
                  className="p-4 rounded-lg border hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex gap-3 flex-1">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{resource.title}</h4>
                          <Badge variant="outline" className="text-xs">
                            {resource.type === 'caso' ? 'Caso' : 
                             resource.type === 'chat' ? 'Chat' : 
                             resource.type === 'document' ? 'Documento' : resource.type}
                          </Badge>
                        </div>
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <p className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            Escritório: {resource.firmName}
                          </p>
                          <p>
                            Compartilhado por: {resource.sharedBy.name}
                          </p>
                          <p>
                            {formatDistanceToNow(resource.sharedAt, {
                              addSuffix: true,
                              locale: ptBR,
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                    {resource.canManage && (resource.type === 'caso' || resource.type === 'chat') && (
                      <div className="flex items-center">
                        <ActionButton
                          icon={Trash2}
                          label="Remover"
                          variant="reject"
                          onClick={() => handleDeleteClick(resource)}
                          className="h-9"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      
      <UnshareConfirmDialog
        open={unshareDialog.open}
        onOpenChange={(open) =>
          setUnshareDialog({ open, resource: unshareDialog.resource })
        }
        onConfirm={handleConfirmUnshare}
        resourceTitle={unshareDialog.resource?.title || ''}
        resourceType={unshareDialog.resource?.type || ''}
        memberCount={unshareDialog.resource?.memberCount || 0}
      />
    </Card>
  );
}
